import { AfterContentInit, Component, HostListener, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HeaderRights } from './header-rights';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { OrganizationsNavigationProvider } from '../../../organizations/providers/navigation-provider';
import { of, Subscription, switchMap, tap } from 'rxjs';
import { UserDefaultHomepageService } from "../../providers/user-default-homepage.service";
import { MenuItem } from "../../models/menu";
import { Organization } from "../../models/organization";
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../providers/utils-provider.service";
import { CurrentUser } from "../../providers/current-user";
import { UsersProvider } from "../../../users/providers/users-provider";
import { catchError, map } from "rxjs/operators";
import { OrganizationMembership } from "../../models/organization-membership";
import { HeaderMainMenusComponent } from "./main-menus/main-menus";
import { MatMiniFabButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { TranslateModule } from "@ngx-translate/core";
import { MatIcon } from "@angular/material/icon";
import { LocationStrategy, NgIf, NgFor, NgOptimizedImage } from "@angular/common";
import { DiscussionListComponent } from "../../../chat/discussion/discussion-list.component";
import { LangsProvider, Lang } from "../../../common/providers/langs-provider";
import {
  NotificationsNotificationsMenuComponent
} from "../../../notifications/components/notifications-menu/notifications-menu";
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'header-elitment',
  providers: [UserDefaultHomepageService],
  styleUrls: ['./header-elitment.scss'],
  standalone: true,
  imports: [
    HeaderMainMenusComponent,
    RouterLink,
    MatMiniFabButton,
    MatTooltip,
    MatMenuTrigger,
    TranslateModule,
    MatIcon,
    MatMenu,
    MatMenuItem,
    NgIf,
    NgFor,
    NgOptimizedImage,
    DiscussionListComponent,
    NotificationsNotificationsMenuComponent
],
  templateUrl: './header-elitment.html'
})
export class HeaderComponent implements AfterContentInit, OnDestroy {
    @Input() public backMenu: MenuItem | null = null;
    @Input() public pageTitle: string = '';
    @Input() public pageSubtitle: string = '';
    @Input() public loginNextUrl: string = '';
    @Input() public loginText = 'Connection';
    @Input() public hasSideMenu = false;
    @Input() public headerMarginBottom = 32;
    @Input() public logoAlwaysDisplayed = true;

    public hasScrolled = false;
    public dashboardUrl = '/organizations';
    public rights: HeaderRights = new HeaderRights(this.utils);
    public organization: Organization | null = null;
    public availableLangs: Lang[] = [];
    public appVersion = environment.version;

    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        public utils: UtilsProvider,
        public currentUser: CurrentUser,
        private _router: Router,
        private _route: ActivatedRoute,
        private _utils: UtilsProvider,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private usersProvider: UsersProvider,
        private langsProvider: LangsProvider,
        private userDefaultHomepageService: UserDefaultHomepageService,
    ) { }

    public ngAfterContentInit() {
      this.availableLangs = this.langsProvider.getLangs()

      this.subscriptions.push(
        this.userDefaultHomepageService.getDashboardUrl().subscribe((url) => {
          this.dashboardUrl = url;
        })
      );

      // S'abonner au flux principal des organisations
      this.subscriptions.push(
        this.currentUser.getOrganizationsMemberships().pipe(
          switchMap((organizationMemberships: OrganizationMembership[]) => {
            if (organizationMemberships && organizationMemberships.length === 1) {
              this.organization = organizationMemberships[0].organization!;
              if (this.organization?.id) {
                const roles = this._currentUserRoles.getRolesIds(this.organization.id);
                this.rights.evaluateScreenRights({ rolesIds: roles });
              }
              // Retourner un observable neutre pour continuer la chaîne
              return of(null);
            } else {
              // Si l'utilisateur est lié à plusieurs organisations, récupérer l'organisation depuis la route
              return this._organizationNavigation.getOrganisationFromRoute().pipe(
                tap((organization: Organization | null) => {
                  this.organization = organization;
                  if (this.organization?.id) {
                    const roles = this._currentUserRoles.getRolesIds(this.organization.id);
                    this.rights.evaluateScreenRights({ rolesIds: roles });
                  }
                }),
                // Gérer les erreurs potentielles ou les cas où aucun résultat n'est retourné
                catchError(err => {
                  console.error("Header GetOrg : ", err);
                  return of(null); // Fournir une continuation neutre
                })
              );
            }
          })
        ).subscribe()
      );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public onLogoutClicked(): void {
        this._router.navigate(['logout']);
    }

    public onLangClicked(langId: number): void {
        if (!this.currentUser.user) return;
        if (this.currentUser.user.langId !== langId) {
            const user = this.currentUser.user;
            user.langId = langId;
            this.usersProvider.updateUser(user.id!, user).subscribe({
                next: () => {
                    this.currentUser.user = user;
                    window.location.reload();
                },
                error: (error: any) => this.onError(error)
            })
        }
    }

    @HostListener('window:scroll', []) public onWindowsScrolled(evend: any): void {
        this.hasScrolled = document.getElementsByTagName('html')[0].scrollTop > this.headerMarginBottom;
    }

    public onBackClicked(): void {
        if (!this.backMenu) {
            this._router.navigate(['/']);
            return;
        }
        if (this.backMenu.onClicked) {
            this.backMenu.onClicked();
            return;
        }
        if (!this.backMenu.url) {
            this._router.navigate(['/']);
            return;
        }
        this._router.navigateByUrl(this.backMenu.url);
    }

    private onError(error: any): void {
      console.error("Header : ", error);
    }
}
